import { Scale, FileCheck, Shield, BookOpen, AlertTriangle, Search, Building2, Brain, Users, PoundSterling, Leaf } from 'lucide-react';
import { Product } from './types';

export const legalProducts: Product[] = [
  {
    id: 'compliance-monitoring-system',
    title: 'Compliance Monitoring System',
    subtitle: 'Automated compliance tracking',
    description: 'Comprehensive compliance management with automated monitoring, reporting, and risk assessment across UK regulatory frameworks including FCA, ICO, and HSE.',
    icon: Shield,
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Legal & Compliance',
    status: 'Available',
    features: [
      'UK Multi-framework compliance (FCA, ICO, HSE)',
      'Automated monitoring & alerts',
      'Risk assessment & mitigation',
      'Comprehensive audit trails',
      'UK regulatory updates & notifications',
      'Customisable reporting & dashboards',
      'Alert management & escalation',
      'Document control & version management'
    ],
    pricing: { setup: 48000, monthly: 2208 },
    color: 'from-blue-500 to-indigo-600',
    businessTypes: ['#compliance', '#legal', '#risk', '#audit', '#governance']
  },
  {
    id: 'contract-lifecycle-management',
    title: 'Contract Lifecycle Management',
    subtitle: 'End-to-end contract control',
    description: 'Full contract lifecycle management with automated creation, negotiation, approval, and tracking.',
    icon: FileCheck,
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Legal & Compliance',
    status: 'Available',
    features: [
      'Contract creation',
      'Automated workflows',
      'Version control',
      'E-signatures',
      'Compliance checks',
      'Renewal reminders',
      'Reporting tools',
      'Integration capabilities'
    ],
    pricing: { setup: 42000, monthly: 1932 },
    color: 'from-purple-500 to-indigo-600',
    businessTypes: ['#contracts', '#legal', '#automation', '#compliance', '#workflow']
  },
  {
    id: 'legal-research-platform',
    title: 'Legal Research Platform',
    subtitle: 'AI-powered legal insights',
    description: 'Advanced legal research platform with AI-driven analytics, case law analysis, and regulatory tracking.',
    icon: BookOpen,
    image: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Legal & Compliance',
    status: 'Available',
    features: [
      'AI-driven analytics',
      'Case law analysis',
      'Regulatory tracking',
      'Legal news updates',
      'Expert commentary',
      'Custom alerts',
      'Collaboration tools',
      'Mobile access'
    ],
    pricing: { setup: 38000, monthly: 1748 },
    color: 'from-blue-500 to-indigo-600',
    businessTypes: ['#legal-research', '#ai', '#analytics', '#law', '#compliance']
  },
  {
    id: 'litigation-management-system',
    title: 'Litigation Management System',
    subtitle: 'Streamlined case management',
    description: 'Comprehensive litigation management with case tracking, document management, calendaring, and reporting.',
    icon: Scale,
    image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Legal & Compliance',
    status: 'Available',
    features: [
      'Case tracking',
      'Document management',
      'Calendaring',
      'Task management',
      'Time tracking',
      'Reporting tools',
      'Collaboration tools',
      'Mobile access'
    ],
    pricing: { setup: 45000, monthly: 2070 },
    color: 'from-gray-500 to-slate-600',
    businessTypes: ['#litigation', '#legal', '#case-management', '#documents', '#reporting']
  },
  {
    id: 'incident-reporting-system',
    title: 'Incident Reporting System',
    subtitle: 'Proactive risk management',
    description: 'Centralized incident reporting system with automated alerts, investigation workflows, and compliance reporting.',
    icon: AlertTriangle,
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Legal & Compliance',
    status: 'Available',
    features: [
      'Incident reporting',
      'Automated alerts',
      'Investigation workflows',
      'Root cause analysis',
      'Corrective actions',
      'Compliance reporting',
      'Trend analysis',
      'Mobile access'
    ],
    pricing: { setup: 35000, monthly: 1610 },
    color: 'from-orange-500 to-red-600',
    businessTypes: ['#incident-reporting', '#risk-management', '#compliance', '#safety', '#alerts']
  },
  {
    id: 'gdpr-compliance-manager',
    title: 'GDPR Compliance Manager',
    subtitle: 'Data privacy compliance platform',
    description: 'Comprehensive GDPR compliance management with data mapping, consent management, breach notifications, and privacy impact assessments.',
    icon: Shield,
    image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Legal & Compliance',
    status: 'Available',
    features: [
      'Data mapping & inventory',
      'Consent management',
      'Breach notification system',
      'Privacy impact assessments',
      'Data subject requests',
      'Cookie consent management',
      'Audit trails',
      'Compliance reporting',
      'Staff training modules',
      'Third-party risk assessment'
    ],
    pricing: { setup: 50000, monthly: 2300 },
    color: 'from-blue-500 to-indigo-600',
    businessTypes: ['#gdpr', '#privacy', '#compliance', '#data-protection', '#consent']
  },
  {
    id: 'intellectual-property-management',
    title: 'Intellectual Property Management',
    subtitle: 'IP portfolio optimization',
    description: 'Complete IP management system with patent tracking, trademark monitoring, licensing management, and IP analytics.',
    icon: BookOpen,
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Legal & Compliance',
    status: 'Available',
    features: [
      'Patent portfolio tracking',
      'Trademark monitoring',
      'License management',
      'IP valuation tools',
      'Renewal reminders',
      'Prior art searches',
      'Infringement monitoring',
      'Revenue tracking',
      'Global IP database',
      'Analytics & reporting'
    ],
    pricing: { setup: 55000, monthly: 2530 },
    color: 'from-purple-500 to-indigo-600',
    businessTypes: ['#intellectual-property', '#patents', '#trademarks', '#licensing', '#ip-management']
  },
  {
    id: 'regulatory-change-management',
    title: 'Regulatory Change Management',
    subtitle: 'Regulatory tracking and compliance',
    description: 'Proactive regulatory change management with automated monitoring, impact assessment, and compliance updates.',
    icon: AlertTriangle,
    image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Legal & Compliance',
    status: 'Available',
    features: [
      'Regulatory monitoring',
      'Change notifications',
      'Impact assessments',
      'Compliance updates',
      'Risk analysis',
      'Action planning',
      'Stakeholder notifications',
      'Compliance tracking',
      'Regulatory library',
      'Reporting tools'
    ],
    pricing: { setup: 52000, monthly: 2392 },
    color: 'from-orange-500 to-red-600',
    businessTypes: ['#regulatory', '#compliance', '#monitoring', '#risk', '#change-management']
  },
  {
    id: 'legal-document-automation',
    title: 'Legal Document Automation',
    subtitle: 'Automated legal document generation',
    description: 'Streamline legal document creation with templates, automated generation, e-signatures, and workflow management.',
    icon: FileCheck,
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Legal & Compliance',
    status: 'Available',
    features: [
      'Document templates',
      'Automated generation',
      'Conditional logic',
      'E-signature integration',
      'Version control',
      'Approval workflows',
      'Data integration',
      'Bulk generation',
      'Audit trails',
      'Custom branding'
    ],
    pricing: { setup: 40000, monthly: 1840 },
    color: 'from-green-500 to-teal-600',
    businessTypes: ['#legal-automation', '#documents', '#templates', '#workflow', '#efficiency']
  },
  {
    id: 'ethics-whistleblower-platform',
    title: 'Ethics & Whistleblower Platform',
    subtitle: 'Anonymous reporting and ethics management',
    description: 'Secure ethics and whistleblower platform with anonymous reporting, case management, and compliance tracking.',
    icon: Scale,
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Legal & Compliance',
    status: 'Available',
    features: [
      'Anonymous reporting',
      'Case management',
      'Investigation workflows',
      'Secure messaging',
      'Compliance tracking',
      'Trend analysis',
      'Multi-language support',
      'Mobile reporting',
      'Audit capabilities',
      'Regulatory reporting'
    ],
    pricing: { setup: 38000, monthly: 1748 },
    color: 'from-gray-500 to-slate-600',
    businessTypes: ['#ethics', '#whistleblower', '#compliance', '#reporting', '#investigations']
  },
  {
    id: 'ediscovery-digital-evidence',
    title: 'E-Discovery & Digital Evidence Platform',
    subtitle: 'Enterprise litigation support system',
    description: 'Comprehensive e-discovery platform with legal hold management, AI-powered document review, privilege screening, and digital evidence processing for litigation and investigations.',
    icon: Search,
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Legal & Compliance',
    status: 'Available',
    features: [
      'Legal hold management',
      'AI document review',
      'Privilege review automation',
      'Data collection & processing',
      'Advanced search & filtering',
      'Production management',
      'Cost tracking & analytics',
      'Multi-format support',
      'Forensic data recovery',
      'Chain of custody tracking',
      'Expert witness reports',
      'Compliance certifications'
    ],
    pricing: { setup: 65000, monthly: 2990 },
    color: 'from-blue-600 to-indigo-700',
    businessTypes: ['#ediscovery', '#litigation', '#forensics', '#legal-tech', '#document-review']
  },
  {
    id: 'enterprise-legal-management',
    title: 'Enterprise Legal Management (ELM)',
    subtitle: 'Corporate legal operations platform',
    description: 'Complete legal operations management with matter management, e-billing, vendor management, legal spend analytics, and performance tracking for corporate legal departments.',
    icon: Building2,
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Legal & Compliance',
    status: 'Available',
    features: [
      'Matter lifecycle management',
      'E-billing & invoice processing',
      'Outside counsel management',
      'Legal spend analytics',
      'Budget planning & tracking',
      'Performance metrics',
      'Workflow automation',
      'Document repository',
      'Reporting dashboards',
      'Vendor evaluation tools',
      'Cost allocation',
      'Regulatory compliance tracking'
    ],
    pricing: { setup: 58000, monthly: 2668 },
    color: 'from-green-600 to-teal-700',
    businessTypes: ['#legal-operations', '#matter-management', '#legal-spend', '#corporate-legal', '#vendor-management']
  },
  {
    id: 'ai-contract-analytics',
    title: 'AI-Powered Contract Analytics & Risk Assessment',
    subtitle: 'Intelligent contract risk management',
    description: 'Advanced AI contract analytics platform with automated risk scoring, clause extraction, obligation tracking, and predictive contract insights for enterprise contract portfolios.',
    icon: Brain,
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Legal & Compliance',
    status: 'Available',
    features: [
      'AI contract risk scoring',
      'Automated clause extraction',
      'Obligation identification',
      'Contract comparison analysis',
      'Renewal risk assessment',
      'Compliance gap detection',
      'Contract performance metrics',
      'Predictive analytics',
      'Language standardization',
      'Risk heat maps',
      'Negotiation insights',
      'Portfolio optimization'
    ],
    pricing: { setup: 52000, monthly: 2392 },
    color: 'from-purple-600 to-indigo-700',
    businessTypes: ['#contract-analytics', '#ai-legal', '#risk-assessment', '#contract-management', '#predictive-analytics']
  },
  {
    id: 'corporate-governance-board',
    title: 'Corporate Governance & Board Management',
    subtitle: 'Enterprise governance platform',
    description: 'Comprehensive corporate governance solution with board portal, meeting management, entity management, and governance compliance for public and private companies.',
    icon: Users,
    image: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Legal & Compliance',
    status: 'Available',
    features: [
      'Secure board portal',
      'Meeting scheduling & management',
      'Document distribution',
      'Voting & resolutions',
      'Entity management',
      'Subsidiary tracking',
      'Compliance calendars',
      'Director & officer insurance',
      'Governance reporting',
      'Audit committee tools',
      'Risk oversight',
      'Regulatory filings'
    ],
    pricing: { setup: 45000, monthly: 2070 },
    color: 'from-slate-600 to-gray-700',
    businessTypes: ['#corporate-governance', '#board-management', '#entity-management', '#compliance', '#public-companies']
  },
  {
    id: 'aml-financial-crime',
    title: 'Anti-Money Laundering (AML) & Financial Crime Platform',
    subtitle: 'Financial crime detection & compliance',
    description: 'Advanced AML and financial crime platform with transaction monitoring, sanctions screening, customer due diligence, and regulatory reporting for financial institutions.',
    icon: PoundSterling,
    image: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Legal & Compliance',
    status: 'Available',
    features: [
      'Real-time transaction monitoring',
      'Sanctions list screening',
      'Customer due diligence (CDD)',
      'Suspicious activity reporting (SAR)',
      'Know Your Customer (KYC)',
      'Enhanced due diligence (EDD)',
      'Regulatory reporting automation',
      'Risk scoring algorithms',
      'Case management',
      'Audit trail management',
      'Compliance training modules',
      'Global sanctions databases'
    ],
    pricing: { setup: 60000, monthly: 2760 },
    color: 'from-red-600 to-orange-700',
    businessTypes: ['#aml', '#financial-crime', '#compliance', '#kyc', '#sanctions-screening']
  },
  {
    id: 'esg-reporting-platform',
    title: 'Environmental, Social & Governance (ESG) Reporting',
    subtitle: 'Sustainable business reporting',
    description: 'Comprehensive ESG reporting platform with sustainability metrics, stakeholder engagement, regulatory compliance, and impact measurement for enterprise sustainability initiatives.',
    icon: Leaf,
    image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Legal & Compliance',
    status: 'Available',
    features: [
      'ESG data collection & management',
      'Sustainability reporting automation',
      'Carbon footprint tracking',
      'Supply chain monitoring',
      'Stakeholder engagement tools',
      'Regulatory framework compliance',
      'Impact measurement & analytics',
      'Third-party verification',
      'Benchmark comparisons',
      'Risk assessment tools',
      'Goal setting & tracking',
      'Investor relations support'
    ],
    pricing: { setup: 55000, monthly: 2530 },
    color: 'from-green-600 to-emerald-700',
    businessTypes: ['#esg', '#sustainability', '#environmental', '#social-impact', '#governance-reporting']
  }
];
